import React from "react";
import { Button } from "antd";
import lib from "../../assets/utils/lib";
import { getFacilityPlanType, getFacilityTypeName } from "../facilities/facilityUtils";
import { formatSearchDate, isActiveStatus } from "./searchResultUtils";
import { ClientListingCard } from "../clients/ClientListingCard";
import { EnrolleeListingCard } from "../clients/EnrolleeListingCard";
import { TariffItemListingCard } from "./TariffItemListingCard";
import { AppointmentListingCard } from "../appointments/AppointmentListingCard";
import { EncounterListingCard } from "../checkins/EncounterListingCard";
import {
  SettingOutlined,
  UsergroupAddOutlined,
  LinkOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../assets/utils/useAuth";
import {
  canManageEncounterCodes,
  EnrolleeEncounterCodeQuickActions,
} from "../compoinents/EnrolleeEncounterCodes";
import {
  canRevealOnboardingOtp,
  OnboardingOtpRevealAction,
} from "../compoinents/OnboardingOtpReveal";

/**
 * Result cards shared by /search and the global search overlay.
 * `listTarget` is the active search category (e.g. clients, enrollees).
 */
export const ClientCard = ({ record, onAction, listTarget }) => (
  <ClientListingCard
    record={record}
    actions={
      <>
        <Button
          type="link"
          icon={<SettingOutlined />}
          onClick={() => onAction(listTarget)}
          className="listing-card__action"
          size="small"
        >
          Settings
        </Button>
        <Button
          type="link"
          icon={<UsergroupAddOutlined />}
          onClick={() => onAction("client_enrollees")}
          className="listing-card__action"
          size="small"
        >
          Enrollees
        </Button>
      </>
    }
  />
);

export const EnrolleeCard = ({ record, onAction, listTarget }) => {
  const { permissions } = useAuth();
  const showEncounterCode =
    canManageEncounterCodes(permissions) && isActiveStatus(record?.status);

  return (
    <EnrolleeListingCard
      record={record}
      actions={
        <>
          <Button
            type="link"
            size="small"
            className="listing-card__action"
            icon={<SettingOutlined />}
            onClick={() => onAction(listTarget)}
          >
            Settings
          </Button>
          {showEncounterCode ? (
            <EnrolleeEncounterCodeQuickActions
              record={record}
              onOpenPanel={() => onAction("enrollee_encounter_codes")}
            />
          ) : null}
        </>
      }
    />
  );
};

export const FacilityCard = ({ record, onAction, listTarget }) => {
  const facilityType = getFacilityTypeName(record);
  const planType = getFacilityPlanType(record);

  return (
  <div className="card-content">
    <div className="card-header">
      <div className="card-title">{record?.facility_name}</div>
      <div className="card-status">
        {isActiveStatus(record.facility_status) ? (
          <span className="status-active">
            <CheckOutlined /> Active
          </span>
        ) : (
          <span className="status-inactive">
            <CloseOutlined /> Inactive
          </span>
        )}
      </div>
    </div>

    <div className="card-body">
      <div className="card-section">
        <div className="card-label">Facility Information</div>
        <div className="card-info">
          <div className="info-item">
            <span className="info-label">Type:</span>
            <span className="info-value">{facilityType ? facilityType.toUpperCase() : "—"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Plan Type:</span>
            <span className="info-value">{planType ? planType.toUpperCase() : "—"}</span>
          </div>
        </div>
      </div>

      <div className="card-section">
        <div className="card-label">Contact Information</div>
        <div className="card-info">
          <div className="info-item">
            <span className="info-label">Phone:</span>
            <span className="info-value">{record?.facility_phone}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Email:</span>
            <span className="info-value">{record?.facility_email}</span>
          </div>
          {record?.facility_website && (
            <div className="info-item">
              <span className="info-label">Website:</span>
              <span className="info-value">{record?.facility_website}</span>
            </div>
          )}
        </div>
      </div>

      <div className="card-section">
        <div className="card-label">Location</div>
        <div className="card-info">
          <div className="info-item">
            <span className="info-label">Address:</span>
            <span className="info-value">{record?.facility_address}</span>
          </div>
          <div className="info-item">
            <span className="info-label">City/State:</span>
            <span className="info-value">
              {record?.facility_city} | {record?.facility_state}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div className="card-actions">
      <Button
        type="link"
        icon={<SettingOutlined />}
        onClick={() => onAction(listTarget)}
        className="action-button"
      >
        Settings
      </Button>
    </div>
  </div>
  );
};

export const CorporationCard = ({ record }) => (
  <div className="card-content">
    <div className="card-header">
      <div className="card-title">{record?.org_name}</div>
      <div className="card-status">
        {record.org_status ? (
          <span className="status-active">
            <CheckOutlined /> Active
          </span>
        ) : (
          <span className="status-inactive">
            <CloseOutlined /> Inactive
          </span>
        )}
      </div>
    </div>

    <div className="card-body">
      <div className="card-section">
        <div className="card-label">Organization Details</div>
        <div className="card-info">
          <div className="info-item">
            <span className="info-label">Code:</span>
            <span className="info-value">{record?.org_social_no || record?.code}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Email:</span>
            <span className="info-value">{record?.org_email}</span>
          </div>
        </div>
      </div>

      <div className="card-section">
        <div className="card-label">Location</div>
        <div className="card-info">
          <div className="info-item">
            <span className="info-label">Address:</span>
            <span className="info-value">{record?.org_address}</span>
          </div>
          <div className="info-item">
            <span className="info-label">City/State:</span>
            <span className="info-value">
              {record?.org_city} {record?.org_state}
            </span>
          </div>
        </div>
      </div>

      <div className="card-section">
        <div className="card-label">Important Dates</div>
        <div className="card-info">
          <div className="info-item">
            <span className="info-label">Onboarded:</span>
            <span className="info-value">
              {record?.org_joined_date ? new Date(record.org_joined_date).toDateString() : "N/A"}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Last Renewal:</span>
            <span className="info-value">{(new Date(record?.org_renew_date)).toDateString()}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Next Renewal:</span>
            <span className="info-value">{(new Date(record?.org_expiry_date)).toDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const ItemCard = ({ record }) => <TariffItemListingCard record={record} />;

export const PACard = ({ record, onAction, listTarget }) => (
  <div className="card-content">
    <div className="card-header">
      <div className="card-title">
        {record?.first_name} {record?.surname}
      </div>
      <div className="card-status">
        <span className="status-active">PA check-in</span>
      </div>
    </div>
    <div className="card-body">
      <div className="card-section">
        <div className="card-label">Encounter</div>
        <div className="card-info">
          <div className="info-item">
            <span className="info-label">Check-in ID:</span>
            <span className="info-value">{record?.checkin_id}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Date:</span>
            <span className="info-value">{formatSearchDate(record?.checkin_date)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Insurance No:</span>
            <span className="info-value">{record?.insurance_no}</span>
          </div>
        </div>
      </div>
      <div className="card-section">
        <div className="card-label">Facility</div>
        <div className="card-info">
          <div className="info-item">
            <span className="info-label">Name:</span>
            <span className="info-value">{record?.facility_name}</span>
          </div>
        </div>
      </div>
    </div>
    <div className="card-actions">
      <Button
        type="link"
        icon={<LinkOutlined />}
        onClick={() => onAction(listTarget)}
        className="action-button"
      >
        Open PA
      </Button>
    </div>
  </div>
);

export const ClaimsCard = ({ record, onAction, listTarget }) => (
  <div className="card-content">
    <div className="card-header">
      <div className="card-title">
        {record?.first_name} {record?.surname}
      </div>
      <div className="card-status">
        <span className="status-active">Claim #{record?.claims_id}</span>
      </div>
    </div>
    <div className="card-body">
      <div className="card-section">
        <div className="card-label">Claim</div>
        <div className="card-info">
          <div className="info-item">
            <span className="info-label">Claims ID:</span>
            <span className="info-value">{record?.claims_id}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Check-in:</span>
            <span className="info-value">
              {record?.checkin_code ?? record?.checkin_id}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Date:</span>
            <span className="info-value">{formatSearchDate(record?.checkin_date)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Insurance No:</span>
            <span className="info-value">{record?.insurance_no}</span>
          </div>
        </div>
      </div>
    </div>
    <div className="card-actions">
      <Button
        type="link"
        icon={<LinkOutlined />}
        onClick={() => onAction(listTarget)}
        className="action-button"
      >
        Open claim
      </Button>
    </div>
  </div>
);

export const OnboardingCard = ({ record, onAction, listTarget }) => {
  const { permissions } = useAuth();
  const showOtpReveal = canRevealOnboardingOtp(permissions) && Boolean(record?.phone);

  return (
    <div className="card-content">
      <div className="card-header">
        <div className="card-title">{record?.name}</div>
        <div className="card-status">
          {isActiveStatus(record?.status) ? (
            <span className="status-active">
              <CheckOutlined /> Active
            </span>
          ) : (
            <span className="status-inactive">
              <CloseOutlined /> Lead
            </span>
          )}
        </div>
      </div>
      <div className="card-body">
        <div className="card-section">
          <div className="card-label">Lead</div>
          <div className="card-info">
            <div className="info-item">
              <span className="info-label">Phone:</span>
              <span className="info-value">{record?.phone}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">{record?.email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Org code:</span>
              <span className="info-value">{record?.social_no}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Package:</span>
              <span className="info-value">{record?.insurance_package}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="card-actions">
        <Button
          type="link"
          icon={<LinkOutlined />}
          onClick={() => onAction(listTarget)}
          className="action-button"
        >
          View org
        </Button>
        {showOtpReveal ? <OnboardingOtpRevealAction phone={record?.phone} /> : null}
      </div>
    </div>
  );
};

export const AppointmentCard = ({ record, onAction, listTarget }) => (
  <AppointmentListingCard
    record={record}
    actions={
      <Button
        type="link"
        icon={<LinkOutlined />}
        onClick={() => onAction(listTarget)}
        className="listing-card__action"
        size="small"
      >
        Open
      </Button>
    }
  />
);

export const CheckinSearchCard = ({ record, onAction, listTarget }) => (
  <EncounterListingCard
    record={record}
    actions={
      <Button
        type="link"
        icon={<LinkOutlined />}
        onClick={() => onAction(listTarget)}
        className="listing-card__action"
        size="small"
      >
        Open
      </Button>
    }
  />
);

export function SearchResultsSkeleton({ count = 6 }) {
  return (
    <div className="search-results-grid" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="search-result-card search-result-card--skeleton">
          <div className="dash-skeleton" style={{ height: 14, width: "55%", marginBottom: 12 }} />
          <div className="dash-skeleton" style={{ height: 11, width: "90%", marginBottom: 8 }} />
          <div className="dash-skeleton" style={{ height: 11, width: "75%", marginBottom: 8 }} />
          <div className="dash-skeleton" style={{ height: 11, width: "82%" }} />
        </div>
      ))}
    </div>
  );
}

export function SearchResultsGrid({ listTarget, results, onRowAction }) {
  if (!Array.isArray(results) || results.length === 0) return null;
  const resource =
    lib.unifiedSearchTargetToResource(listTarget) || listTarget;
  const isLinear =
    listTarget === "clients" ||
    listTarget === "enrollees" ||
    listTarget === "appointments" ||
    listTarget === "checkins";
  return (
    <div className={`search-results-grid ${isLinear ? "search-results-grid--linear" : ""}`}>
      {results.map((record, index) => {
        const key = lib.unifiedSearchRowKey(resource, record, index);
        if (listTarget === "clients") {
          return (
            <ClientCard
              key={key}
              record={record}
              onAction={(action) => onRowAction(record, action)}
              listTarget={listTarget}
            />
          );
        }
        if (listTarget === "constableItems") {
          return <ItemCard key={key} record={record} />;
        }
        if (listTarget === "appointments") {
          return (
            <AppointmentCard
              key={key}
              record={record}
              onAction={(action) => onRowAction(record, action)}
              listTarget={listTarget}
            />
          );
        }
        if (listTarget === "checkins") {
          return (
            <CheckinSearchCard
              key={key}
              record={record}
              onAction={(action) => onRowAction(record, action)}
              listTarget={listTarget}
            />
          );
        }
        return (
          <div key={key} className="search-result-card">
            {listTarget === "enrollees" && (
              <EnrolleeCard
                record={record}
                onAction={(action) => onRowAction(record, action)}
                listTarget={listTarget}
              />
            )}
            {listTarget === "facilities" && (
              <FacilityCard
                record={record}
                onAction={(action) => onRowAction(record, action)}
                listTarget={listTarget}
              />
            )}
            {listTarget === "corporations" && <CorporationCard record={record} />}
            {listTarget === "pa" && (
              <PACard
                record={record}
                onAction={(action) => onRowAction(record, action)}
                listTarget={listTarget}
              />
            )}
            {listTarget === "claims" && (
              <ClaimsCard
                record={record}
                onAction={(action) => onRowAction(record, action)}
                listTarget={listTarget}
              />
            )}
            {listTarget === "onboarding" && (
              <OnboardingCard
                record={record}
                onAction={(action) => onRowAction(record, action)}
                listTarget={listTarget}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
